import { Component, input, signal } from '@angular/core';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-panel',
  imports: [],
  template: `
    <div [class]="panelClasses">
      @if (header() || toggleable()) {
        <div 
          [class]="headerClasses"
          (click)="toggle()"
          [attr.aria-expanded]="collapsed()"
          [attr.aria-controls]="contentId">
          <div class="flex items-center gap-3">
            @if (icon()) {
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="icon()" />
              </svg>
            }
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ header() }}</h3>
          </div>
          @if (toggleable()) {
            <svg 
              class="w-5 h-5 transition-transform duration-200"
              [class.rotate-180]="!collapsed()"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          }
        </div>
      }
      <div
        [id]="contentId"
        [class]="contentClasses"
        [attr.hidden]="collapsed()">
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styles: []
})
export class PanelComponent {
  header = input<string>('');
  icon = input<string>('');
  toggleable = input<boolean>(false);
  collapsed = input<boolean>(false);
  variant = input<'default' | 'outlined' | 'elevated'>('default');
  size = input<ComponentSize>('md');

  contentId = `panel-content-${Math.random().toString(36).substr(2, 9)}`;
  collapsedState = signal(this.collapsed());

  toggle(): void {
    if (this.toggleable()) {
      this.collapsedState.update(v => !v);
    }
  }

  get panelClasses(): string {
    const classes = ['rounded-lg', 'overflow-hidden', 'transition-all', 'duration-200'];
    
    if (this.variant() === 'outlined') {
      classes.push('border', 'border-gray-200', 'dark:border-gray-700');
    } else if (this.variant() === 'elevated') {
      classes.push('shadow-lg');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'border', 'border-gray-200', 'dark:border-gray-700');
    }

    return classes.join(' ');
  }

  get headerClasses(): string {
    const classes = [
      'px-4',
      'py-3',
      'flex',
      'items-center',
      'justify-between',
      'bg-gray-50',
      'dark:bg-gray-700',
      'border-b',
      'border-gray-200',
      'dark:border-gray-600'
    ];

    if (this.toggleable()) {
      classes.push('cursor-pointer', 'hover:bg-gray-100', 'dark:hover:bg-gray-600');
    }

    return classes.join(' ');
  }

  get contentClasses(): string {
    const paddingMap = {
      xs: 'px-3 py-2',
      sm: 'px-4 py-3',
      md: 'px-6 py-4',
      lg: 'px-8 py-6',
      xl: 'px-10 py-8'
    };
    
    const classes = [
      paddingMap[this.size()],
      'transition-all',
      'duration-300',
      'ease-in-out',
      'bg-white',
      'dark:bg-gray-800'
    ];

    if (this.collapsedState()) {
      classes.push('max-h-0', 'overflow-hidden', 'opacity-0');
    } else {
      classes.push('max-h-[2000px]', 'opacity-100');
    }

    return classes.join(' ');
  }
}

